import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const getErrorMessage = (error: any): string => {
    const code = error?.code;
    switch (code) {
        case 'auth/invalid-email':
            return 'Bitte gib eine gültige E-Mail-Adresse ein.';
        case 'auth/user-disabled':
            return 'Dieser Benutzer wurde deaktiviert.';
        case 'auth/user-not-found':
            return 'Es existiert kein Konto mit dieser E-Mail-Adresse.';
        case 'auth/wrong-password':
            return 'Das eingegebene Passwort ist falsch.';
        case 'auth/invalid-credential':
            return 'Die eingegebene E-Mail oder das Passwort ist falsch. Bitte überprüfe deine Eingaben.';
        case 'auth/too-many-requests':
            return 'Zu viele fehlgeschlagene Versuche. Bitte versuche es später erneut.';
        case 'auth/network-request-failed':
            return 'Netzwerkfehler – bitte überprüfe deine Internetverbindung.';
        default:
            return 'Anmeldung fehlgeschlagen. Bitte versuche es später erneut.';
    }
};

const Login = () => {
    const { currentUser, login } = useAuth();
    const isAnonymous = currentUser?.isAnonymous || false;

    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!email.includes('@') || !email.includes('.')) {
            setError('Bitte gib eine gültige E-Mail-Adresse ein.');
            setLoading(false);
            return;
        }
        if (password.length < 1) {
            setError('Bitte gib dein Passwort ein.');
            setLoading(false);
            return;
        }

        try {
            await login(email, password);
            navigate('/');
        } catch (err: any) {
            if (err.message === 'EMAIL_NOT_VERIFIED') {
                navigate('/verify-email');
            } else {
                setError(getErrorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4 pt-24">
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
                <div className="absolute top-40 left-20 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md"
            >
                <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 md:p-10">
                    <div className="text-center mb-8">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-lg mb-4"
                        >
                            <Sparkles className="w-8 h-8" />
                        </motion.div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Willkommen zurück</h2>
                        <p className="text-gray-600">Melde dich an, um weiterzulernen</p>
                    </div>

                    {isAnonymous && (
                        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-xl mb-4 text-sm">
                            ⚠️ Du nutzt die App bereits ohne Konto. Wenn du dich mit einem bestehenden Konto anmeldest,
                            gehen deine aktuellen Lernsets verloren.
                            <Link to="/register" className="font-semibold underline text-yellow-900 block mt-1">
                                Erstelle stattdessen ein neues Konto, um deine Daten zu behalten.
                            </Link>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-4 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                E-Mail-Adresse
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                    placeholder="deine@email.de"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Passwort
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-12 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                            <label className="flex items-center gap-2 text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                Angemeldet bleiben
                            </label>
                            <Link to="/passwort-vergessen" className="text-indigo-600 hover:text-indigo-800 font-medium">
                                Passwort vergessen?
                            </Link>
                        </div>

                        <motion.button
                            type="submit"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                'Wird geladen...'
                            ) : (
                                <>
                                    <LogIn className="w-5 h-5" />
                                    Einloggen
                                </>
                            )}
                        </motion.button>
                    </form>

                    <p className="text-center text-gray-600 mt-8">
                        Noch kein Konto?{' '}
                        <Link to="/register" className="text-indigo-600 hover:text-indigo-800 font-semibold">
                            Jetzt registrieren
                        </Link>
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default Login;